import { useToast } from "@chakra-ui/react";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyKeys, setNewNewHireJourneyInQueryClient } from "features/NewHireJourney/hooks";
import { pendingNewHireKeys } from "features/PendingNewHire/hooks";
import { apiDeactivateUserPathById, apiUpdateUserRolesPathById, apiUserPathById } from "helpers/url/pages/v1";
import { pick } from "lodash";
import { BasicUser, User, UserId } from "features/User/models";
import apiClient from "services/ApiClient";
import { NEW_HIRE_JOURNEY_FIELDS, USER_FIELDS } from "definitions/constants/fields";
import ROLE from "models/role";
import { adminKeys } from "features/Admin/hooks";
import { UserUpdateFormValues } from "./DynamicForm/formDefinition";
import userBatcher, { userBatcherWithJourneyAssociations } from "./batcher";

export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (filters: string) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  current: () => [...userKeys.all, "current"] as const,
};

export function setNewUserInQueryClient(queryClient: QueryClient, user: User | BasicUser) {
  queryClient.setQueryData(userKeys.detail(user.id), (oldData: User | undefined) => {
    if (!oldData) {
      return user;
    }
    return {
      ...oldData,
      ...user,
    };
  });
}

export const useCurrentUserQuery = (options: { enabled?: boolean; [key: string]: any } = {}) => useQuery<User>({
  queryKey: userKeys.current(),
  staleTime: 1000 * 60 * 3, // 3 minutes
  queryFn: async () => apiClient.get<User>(apiUserPathById("current")),
  ...options,
});

export const userQueryArgs = (id: UserId, withJourneyAssociations: boolean = false) => ({
  queryKey: withJourneyAssociations ? [...userKeys.detail(id), "with_journey_associations"] : userKeys.detail(id),
  staleTime: 1000 * 60 * 3, // 3 minutes
  queryFn: async () => {
    const batcher = withJourneyAssociations ? userBatcherWithJourneyAssociations : userBatcher;
    return batcher.fetch({ id, withJourneyAssociations });
  },
});

export const useUserDetailQuery = (id: UserId, options: { withJourneyAssociations?: boolean; enabled?: boolean; staleTime?: number } = {}) => {
  const { withJourneyAssociations = false, ...queryOptions } = options;
  return useQuery<User | null>({
    ...userQueryArgs(id, withJourneyAssociations),
    ...queryOptions,
  });
};

export const useUserListQuery = (queryString?: string, options: { enabled?: boolean } = {}) => useQuery<User[]>({
  queryKey: queryString ? userKeys.list(queryString) : userKeys.lists(),
  staleTime: 1000 * 60 * 3, // 3 minutes
  queryFn: async () => apiClient.get<User[]>(`${API_ROUTES.users.base}${queryString ? `?${queryString}` : ""}`),
  ...options,
});

export function useUpdateUser(userId: UserId) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (data: Partial<UserUpdateFormValues>) => {
      const user = pick(data, USER_FIELDS);
      const newHireJourney = pick(data, NEW_HIRE_JOURNEY_FIELDS);
      return apiClient.put<User>(apiUserPathById(userId), {
        ...(Object.keys(user).length ? { user } : {}),
        ...(Object.keys(newHireJourney).length ? { newHireJourney } : {}),
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.count() });
      setNewUserInQueryClient(queryClient, res);
      if (res?.newHireJourney) {
        setNewNewHireJourneyInQueryClient(queryClient, res?.newHireJourney);
      }
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useDeactivateUser(user: User, onSuccess?: (resp: any) => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isActive = !user?.deactivatedAt; // true when the user has never been deactivated (deactivatedAt is null)
  return useMutation({
    mutationFn: () => apiClient.post<User>(apiDeactivateUserPathById(user?.id), {}),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.count() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(user?.id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      if (user?.newHireJourney?.id) {
        queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(user?.newHireJourney?.id) });
      }
      toast({
        title: `User has been ${isActive ? "deactivated" : "reactivated"}`,
        status: "success",
      });
      onSuccess?.(resp);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useUpdateUserRoles(user: User, onSuccess?: (resp: any) => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (userRoles: { roles: ROLE[] }) => apiClient.put<User>(apiUpdateUserRolesPathById(user?.id), { user: userRoles }),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.count() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(user?.id) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      if (user?.newHireJourney?.id) {
        queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(user?.newHireJourney?.id) });
      }
      onSuccess?.(resp);
      toast({
        title: "User roles updated",
        status: "success",
      });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}
